import { injectOnce } from './inject-web-component';
import { VippsBrandUnion, VippsButtonBuilder, VippsButtonTriggerTarget } from './types';
import { getUserPreferredLang } from './utils';

export const button = (): VippsButtonBuilder => {
  let elements: Element[] = [];
  let brand: VippsBrandUnion = 'vipps';
  let domSelector: string | null;
  let triggerTarget: VippsButtonTriggerTarget | null = null;
  let onClickHandler: (() => Promise<void> | void) | null = null;
  let isLoading = false;

  const getButtonLabel = () => (brand === 'vipps' ? 'Pay with Vipps' : 'Pay with MobilePay');

  const applyLoadingState = () => {
    elements.forEach((el) => {
      if (el instanceof HTMLButtonElement) {
        el.disabled = isLoading;
        el.textContent = isLoading ? 'Loading...' : getButtonLabel();
        return;
      }

      if (isLoading) {
        el.setAttribute('loading', 'true');
        el.setAttribute('disabled', '');
      } else {
        el.removeAttribute('loading');
        el.removeAttribute('disabled');
      }
    });
  };

  const setLoading = (value: boolean) => {
    isLoading = value;
    applyLoadingState();
  };

  const isPromiseLike = (value: unknown): value is Promise<unknown> => {
    return !!value && typeof (value as Promise<unknown>).then === 'function';
  };

  const handleClick = () => {
    if (isLoading) return;

    const triggerResult = triggerTarget?.open();
    if (isPromiseLike(triggerResult)) {
      setLoading(true);
      triggerResult.finally(() => {
        setLoading(false);
      });
    }

    const handlerResult = onClickHandler?.();
    if (isPromiseLike(handlerResult)) {
      handlerResult.catch((error) => {
        // Prevent unhandled promise rejections from user-provided callbacks.
        console.error('vipps-button: onclick handler failed', error);
      });
    }
  };

  const enhance = () => {
    unsubscribe();
    elements = elements.map((el) => {
      const wc = toWebComponent();
      el.replaceWith(wc);
      return wc;
    });
    applyLoadingState();
    subscribe();
  };

  const unsubscribe = () => {
    elements.forEach((el) => el.removeEventListener('click', handleClick));
  };

  const subscribe = () => {
    elements.forEach((el) => el.addEventListener('click', handleClick));
  };

  const unmount = () => {
    unsubscribe();
    elements.forEach((el) => el.remove());
  };

  const toElement = () => {
    const n = document.createElement('button');

    n.type = 'button';

    const styles = {
      background: brand === 'vipps' ? '#ff5b24' : '#5a78ff',
      borderRadius: '9999px',
      width: brand === 'vipps' ? '191px' : '220px', // minimize the width change when being replaced by web component
      border: 'none',
      color: 'white',
      fontSize: '18.5px',
      fontWeight: 600,
      height: '44px',
      padding: '0 24px',
      fontFamily: 'Vipps, sans-serif',
      cursor: 'pointer',
      verticalAlign: 'middle',
    };

    n.textContent = getButtonLabel();

    Object.entries(styles).forEach(([k, v]) => {
      n.style[k] = v;
    });

    return n;
  };

  const toWebComponent = () => {
    const n = document.createElement('vipps-mobilepay-button');
    n.setAttribute('brand', brand);
    n.setAttribute('language', getUserPreferredLang());
    n.setAttribute('rounded', 'true');
    n.setAttribute('branded', 'true');
    n.setAttribute('verb', 'pay');

    return n;
  };

  injectOnce(enhance);

  return {
    triggers: function (target: VippsButtonTriggerTarget) {
      unsubscribe();
      triggerTarget = target;
      subscribe();

      return this;
    },
    onclick: function (handler: () => Promise<void> | void) {
      unsubscribe();
      onClickHandler = handler;
      subscribe();

      return this;
    },
    brand: function (value: VippsBrandUnion) {
      brand = value;
      applyLoadingState();
      return this;
    },
    mount: function (selector: string) {
      domSelector = selector;
      unmount();
      const isReady = !!customElements.get('vipps-mobilepay-button');
      const found = Array.from(document.querySelectorAll(selector));
      elements = found.map((f) => {
        const n = isReady ? toWebComponent() : toElement();
        f.append(n);

        return n;
      });
      applyLoadingState();
      subscribe();

      return this;
    },
    rerender: function () {
      this.mount(domSelector);

      return this;
    },
    unmount: function () {
      unmount();

      return this;
    },
    toElement,
  };
};
